/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback } from 'preact/hooks';
import { usePress } from '../hooks/UNSAFE_usePress';
import { DropdownArrowIcon } from '../PRIVATE_ThemedIcons/DropdownArrowIcon';
import { classNames } from '../utils/UNSAFE_classNames';
import { dropdownStyles } from './themes/dropdownStyles.css';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { ButtonLabelLayoutRedwoodTheme } from '#UNSAFE_ButtonLabelLayout/themes/redwood/ButtonLabelLayoutTheme';
import type { ButtonLabelLayoutVariantOptions } from '#UNSAFE_ButtonLabelLayout/themes/ButtonLabelLayoutStyles.css';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { BaseButtonRedwoodTheme } from '#UNSAFE_BaseButton/themes/redwood/BaseButtonTheme';

type Props = TestIdProps & {
  isDisabled?: boolean;
  onClick?: () => void;
};

const noop = () => {};

export function DropdownArrow({ isDisabled = false, onClick, testId }: Props) {
  const { classes } = useComponentTheme<ButtonLabelLayoutVariantOptions>(
    ButtonLabelLayoutRedwoodTheme,
    { size: 'sm' }
  );
  const { baseTheme } = useComponentTheme(BaseButtonRedwoodTheme);

  const preventDefault = useCallback((event: Event) => {
    event.preventDefault();
  }, []);
  const { pressProps } = usePress(onClick ?? noop, { isDisabled });

  // TODO: JET-67410 replace this with a icon button
  const styleClasses = classNames([
    classes,
    baseTheme,
    dropdownStyles.arrow.base,
    isDisabled ? dropdownStyles.arrow.disabled : dropdownStyles.arrow.enabled
  ]);

  const testIdProps = useTestId(testId);

  return (
    <span
      aria-hidden="true"
      class={styleClasses}
      tabIndex={-1}
      onMouseDown={preventDefault}
      {...testIdProps}
      {...pressProps}>
      <DropdownArrowIcon />
    </span>
  );
}
